import { Suspense, useEffect, useState, useTransition } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import styled from 'styled-components';

import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { glossary } from '@sorare/core/src/lib/glossary';

import { BlogResults } from 'components/BlogResults';
import { SportTabs } from 'components/Tabs';

const Root = styled(Vertical).attrs({
  gap: 5,
})`
  margin-top: var(--triple-unit);
`;

const SearchWrapper = styled.div`
  max-width: fit-content;
`;

const messages = defineMessages({
  seoTitle: {
    id: 'blog.seo.title',
    defaultMessage: 'Fantasy Sports Reimagined - Sorare Blog',
  },
  seoDescription: {
    id: 'blog.seo.description',
    defaultMessage:
      'Experience football, basketball, and baseball like never before. Collect. Play. Win. #OwnYourGame.',
  },
});

const Home = () => {
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [, setSearchParams] = useSearchParams();
  const [isPending, startTransition] = useTransition();

  useDebounce(
    () => {
      if (search !== debouncedSearch) {
        startTransition(() => {
          setDebouncedSearch(search);
        });
      }
    },
    300,
    [search]
  );

  useEffect(() => {
    if (debouncedSearch) {
      setSearchParams(`search=${debouncedSearch}`);
    } else {
      setSearchParams();
    }
  }, [debouncedSearch, setSearchParams]);

  useEffect(() => {
    setPageMetadata(formatMessage(messages.seoTitle), {
      description: formatMessage(messages.seoDescription),
    });
  }, [formatMessage, setPageMetadata]);

  return (
    <Root gap={4}>
      <SportTabs />
      <SearchWrapper>
        <SearchInput
          withIcon
          placeholder={formatMessage(glossary.search)}
          value={search || ''}
          onChange={event => setSearch(event.target.value)}
        />
      </SearchWrapper>
      <Suspense fallback={null}>
        <BlogResults search={debouncedSearch} isSearching={!!isPending} />
      </Suspense>
    </Root>
  );
};
export default Home;
